import { useState } from 'react';
import { StatusChip } from '../../components/data';
import {
  Avatar,
  DeleteButton,
  EmployeePicker,
  ErrorBanner,
  Field,
  Loading,
  Modal,
} from '../../components/ui';
import { api } from '../../lib/api';
import { useResource } from '../../lib/hooks';
import type { Employee } from '../../lib/types';
import type { EosTeam } from './eosTypes';
import { EosSection, type BusinessUnitOption } from './eosUi';

/**
 * Teams — who meets, and about what.
 *
 * EOS cascades: the leadership team runs its L10 and each departmental team
 * runs its own, each with their own scorecard, rocks and issues list. This is
 * where those teams are defined.
 *
 * It is NOT a staff list. People come from `core.Employee`, the directory this
 * app already owns and administers on the Employees page — that app kept its
 * own `users` table and it did not come across, for the reason written out at
 * the top of migration 058. So a person missing from the picker below is a
 * person missing from the directory, and the fix is on that page rather than
 * this one.
 */
export function EosTeams({ onChanged }: { onChanged: () => void }) {
  const teams = useResource<EosTeam[]>('/eos/teams');
  const employees = useResource<Employee[]>('/employees?status=active');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<EosTeam | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = () => {
    teams.reload();
    onChanged();
  };

  return (
    <>
      {error && <ErrorBanner message={error} />}

      <EosSection
        title="Teams"
        note="Each team runs its own Level 10, with its own scorecard, rocks and issues list. People come from the employee directory."
        actions={
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setCreating(true)}>
            Add team
          </button>
        }
      >
        {teams.loading && <Loading label="Loading teams" />}
        {teams.error && <ErrorBanner message={teams.error} />}

        {teams.data?.map((team) => (
          <div key={team.EosTeamId} className="eos-team">
            <div className="row-between wrap" style={{ gap: 12 }}>
              <div className="grow">
                <div className="row" style={{ gap: 8, alignItems: 'center' }}>
                  <span className="strong">{team.Name}</span>
                  {team.IsDefault && <StatusChip band="neutral">Default</StatusChip>}
                  {/* Plain text, not a chip: the unit is context for the
                      meeting, and a second badge beside "Default" would read
                      as a second status. */}
                  {team.BusinessUnitName && (
                    <span className="xs subtle" title="The business review files this meeting's items here">
                      {team.BusinessUnitName}
                    </span>
                  )}
                </div>
                {team.Description && <div className="xs subtle mt-2">{team.Description}</div>}
              </div>
              <div className="row wrap" style={{ gap: 4 }}>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => setEditing(team)}
                >
                  Edit
                </button>
                <DeleteButton
                  label="Retire"
                  what={`the team "${team.Name}"`}
                  onConfirm={() => {
                    void api
                      .del(`/eos/teams/${team.EosTeamId}`)
                      .then(refresh)
                      .catch((err: unknown) =>
                        setError(err instanceof Error ? err.message : 'Could not retire it.'),
                      );
                  }}
                />
              </div>
            </div>

            <div className="mt-4">
              {team.members.length === 0 && (
                <p className="xs subtle">Nobody on this team yet — it cannot hold a meeting.</p>
              )}
              {team.members.map((member) => (
                <div key={member.EmployeeId} className="eos-member">
                  <Avatar name={member.FullName} />
                  <div className="grow">
                    <span>{member.FullName}</span>
                    {member.RoleName && <span className="xs subtle"> · {member.RoleName}</span>}
                  </div>
                  {member.IsLead && <StatusChip band="neutral">Runs the meeting</StatusChip>}
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => {
                      void api
                        .post(`/eos/teams/${team.EosTeamId}/members`, {
                          employeeId: member.EmployeeId,
                          isLead: !member.IsLead,
                        })
                        .then(refresh)
                        .catch((err: unknown) =>
                          setError(err instanceof Error ? err.message : 'Could not update them.'),
                        );
                    }}
                  >
                    {member.IsLead ? 'Not the lead' : 'Make lead'}
                  </button>
                  <DeleteButton
                    label="✕"
                    what={`${member.FullName} from ${team.Name}`}
                    onConfirm={() => {
                      void api
                        .del(`/eos/teams/${team.EosTeamId}/members/${member.EmployeeId}`)
                        .then(refresh)
                        .catch((err: unknown) =>
                          setError(err instanceof Error ? err.message : 'Could not remove them.'),
                        );
                    }}
                  />
                </div>
              ))}

              <AddMember
                employees={(employees.data ?? []).filter(
                  (e) => !team.members.some((m) => m.EmployeeId === e.EmployeeId),
                )}
                onAdd={(employeeId) => {
                  void api
                    .post(`/eos/teams/${team.EosTeamId}/members`, { employeeId })
                    .then(refresh)
                    .catch((err: unknown) =>
                      setError(err instanceof Error ? err.message : 'Could not add them.'),
                    );
                }}
              />
            </div>
          </div>
        ))}
      </EosSection>

      {(creating || editing) && (
        <TeamForm
          team={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            refresh();
          }}
        />
      )}
    </>
  );
}

function AddMember({
  employees,
  onAdd,
}: {
  employees: Employee[];
  onAdd: (employeeId: number) => void;
}) {
  const [employeeId, setEmployeeId] = useState<number | null>(null);

  if (employees.length === 0) {
    return <p className="xs subtle mt-4">Everybody in the directory is already on this team.</p>;
  }

  return (
    <div className="row mt-4" style={{ gap: 8 }}>
      <EmployeePicker
        employees={employees}
        value={employeeId}
        onChange={setEmployeeId}
        placeholder="Add somebody…"
      />
      <button
        type="button"
        className="btn btn-secondary btn-sm"
        disabled={employeeId === null}
        onClick={() => {
          if (employeeId !== null) {
            onAdd(employeeId);
            setEmployeeId(null);
          }
        }}
      >
        Add
      </button>
    </div>
  );
}

function TeamForm({
  team,
  onClose,
  onSaved,
}: {
  team: EosTeam | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(team?.Name ?? '');
  const [description, setDescription] = useState(team?.Description ?? '');
  const [isDefault, setDefault] = useState(team?.IsDefault ?? false);
  const [businessUnitId, setBusinessUnit] = useState<number | null>(team?.BusinessUnitId ?? null);
  /* The same list the item pickers offer, which is deliberately not every unit
     in the directory — only the ones the business review has a section for.
     See the endpoint's own comment in routes/eos.ts. */
  const units = useResource<BusinessUnitOption[]>('/eos/business-units');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!name.trim()) {
      setError('A team needs a name.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const body = {
        name: name.trim(),
        description: description.trim() || null,
        isDefault,
        businessUnitId,
      };
      if (team) await api.put(`/eos/teams/${team.EosTeamId}`, body);
      else await api.post('/eos/teams', body);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.');
      setSaving(false);
    }
  };

  return (
    <Modal
      title={team ? 'Edit team' : 'Add team'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" disabled={saving} onClick={() => void submit()}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}
      <div className="form-grid">
        <Field label="Name">
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Description">
          <input
            className="input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>
        <Field
          label="Business unit"
          hint="Where this meeting's rocks, to-dos and numbers land in the business review. Leave it unset and each item falls back to its owner's unit instead."
        >
          <select
            className="select"
            value={businessUnitId ?? ''}
            onChange={(e) =>
              setBusinessUnit(e.target.value === '' ? null : Number(e.target.value))
            }
          >
            <option value="">No unit — items fall back to their owner&rsquo;s</option>
            {(units.data ?? []).map((u) => (
              <option key={u.BusinessUnitId} value={u.BusinessUnitId}>
                {u.Name}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <label className="checkbox mt-4">
        <input type="checkbox" checked={isDefault} onChange={(e) => setDefault(e.target.checked)} />
        <span>The team the module opens on</span>
      </label>
    </Modal>
  );
}
